import { useGameStore } from './gameStore';
import { useAuthStore } from './authStore';
import { useCloudSyncStore } from './cloudSyncStore';

/**
 * Phase 16.4.3 — Auto-Sync mit 30 s Debounce.
 *
 * `scheduleCloudSync()` wird bei jedem zustand-State-Change in `gameStore`
 * (siehe `subscribeCloudSync()` in main.tsx) aufgerufen. Mehrfach-Aufrufe
 * innerhalb des Debounce-Fensters werden gebündelt.
 *
 * `syncNow()` triggert einen sofortigen Upload (manueller „Jetzt synchronisieren"-
 * Button im Sync-Indicator).
 */
const SYNC_DEBOUNCE_MS = 30_000;

let timer: ReturnType<typeof setTimeout> | null = null;

export function scheduleCloudSync(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    void syncNow();
  }, SYNC_DEBOUNCE_MS);
}

export async function syncNow(): Promise<void> {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated || !auth.token) return;

  const game = useGameStore.getState();
  const slotIndex = (game.activeSlotId ?? 1) - 1; // activeSlotId ist 1..3 → 0..2
  if (slotIndex < 0 || slotIndex > 2) return;

  const cloud = useCloudSyncStore.getState();
  cloud.setStatus('syncing');

  // Daten = aktueller (gesamter) gameStore-State. Ephemere Felder werden
  // bewusst mitgeschickt — der Server sieht nur einen JSON-Blob, das Schema
  // bleibt im Frontend zentralisiert.
  const data = serializeForCloud(game);
  const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

  try {
    const res = await fetch('/api/savegame/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify({ slotIndex, data, version }),
    });
    if (res.ok) {
      const json = (await res.json()) as { updatedAt: number };
      useCloudSyncStore.getState().setSyncedAt(json.updatedAt);
    } else if (res.status === 401) {
      cloud.setStatus('error', 'Session abgelaufen');
      // Auth-Store flippt sich beim nächsten verify selbst aus.
    } else {
      cloud.setStatus('error', `HTTP ${res.status}`);
    }
  } catch (e) {
    cloud.setStatus('offline', String(e));
  }
}

/**
 * Wählt aus dem GameStore-State nur die Felder, die persistiert werden sollen.
 * Spiegelt im Wesentlichen den `partialize`-Block der zustand-persist-Middleware
 * — wir wollen ephemere UI-Flags (mainPanelOpen, selectedDetail …) nicht
 * remote spiegeln.
 */
/** Ephemere Felder, die nicht in den Cloud-Sync-Payload gehören. */
const EPHEMERAL_KEYS: ReadonlySet<string> = new Set([
  'mainPanelOpen',
  'mainPanelTab',
  'selectedDetail',
  'toasts',
  'hydrants',
  'waterFeatures',
  'npcStations',
  'pendingMigrationToast',
  'radioRequests',
]);

function serializeForCloud(state: ReturnType<typeof useGameStore.getState>): unknown {
  // Funktionen + ephemere Felder rausstrippen.
  const src = state as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(src)) {
    if (typeof v === 'function') continue;
    if (EPHEMERAL_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Phase 16.4.5 — Initial-Load nach erfolgreichem Login.
 * Lädt alle 3 Cloud-Slots; ruft `onComplete` mit den Slot-Daten auf, damit
 * das UI den SaveBrowser anstoßen oder einen "lokal-vs-cloud"-Dialog zeigen kann.
 */
export interface CloudSlot {
  slotIndex: number;
  savegame: {
    testerCode: string;
    slotIndex: number;
    data: unknown;
    updatedAt: number;
    version: string;
    size: number;
  } | null;
}

export async function fetchCloudSlots(): Promise<CloudSlot[] | null> {
  const auth = useAuthStore.getState();
  if (!auth.token) return null;
  try {
    const res = await fetch('/api/savegame/list', {
      headers: { Authorization: `Bearer ${auth.token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { slots: CloudSlot[] };
    return data.slots;
  } catch {
    return null;
  }
}
